import { useCallback, useMemo, useState } from 'react';
import type { PersonaDto } from '@/lib/api/personas-client';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import { createComparator } from '@/shared/utils/sorting';
import type { SortDirection } from '@/shared/ui/data-table';
import type { AudienceCreationStatus } from '../../../AudienceStatusChip';
import type { AudienceSortColumn } from '../../../AudiencesTableV2';

interface UseAudiencesListV2Options {
  personas: PersonaDto[] | undefined;
}

export function useAudiencesListV2({ personas }: UseAudiencesListV2Options) {
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);
  const [selectedStatuses, setSelectedStatuses] = useState<AudienceCreationStatus[]>([]);
  const [sortBy, setSortBy] = useState<AudienceSortColumn>('updated');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const visiblePersonas = useMemo(() => {
    const search = debouncedSearch.trim().toLowerCase();

    const filtered = (personas ?? []).filter((persona) => {
      // An empty selection is "no filter", not "match nothing".
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(persona.creationStatus))
        return false;
      if (!search) return true;

      return (
        Boolean(persona.segmentName?.toLowerCase().includes(search)) ||
        Boolean(persona.personaTitle?.toLowerCase().includes(search))
      );
    });

    return filtered.sort(createComparator(sortBy, sortDirection));
  }, [personas, debouncedSearch, selectedStatuses, sortBy, sortDirection]);

  const handleSort = useCallback(
    (column: AudienceSortColumn) => {
      // A newly chosen column starts ascending; the active one reverses.
      if (column === sortBy) {
        setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
        return;
      }
      setSortBy(column);
      setSortDirection('asc');
    },
    [sortBy],
  );

  return {
    searchInput,
    setSearchInput,
    debouncedSearch,
    selectedStatuses,
    setSelectedStatuses,
    sortBy,
    sortDirection,
    handleSort,
    visiblePersonas,
  };
}
